import { spaceManagementService } from "./host-apiServices/spaceManagementService";
import logger from "../utils/logger";


/**
 * AvailabilityService - Centralized service for space availability management
 *
 * Handles fetching and processing availability data from the booking API
 * for all duration types (HOURLY, DAILY, WEEKLY, MONTHLY)
 */
class AvailabilityService {
  /**
   * Get availability for a space
   * @param {number} spaceId - Space ID
   * @param {string} from - Start date (ISO format YYYY-MM-DD)
   * @param {string} to - End date (ISO format YYYY-MM-DD)
   * @param {number} spaceInstanceId - Optional instance ID for multi-instance spaces
   * @returns {Promise<Object>} Availability data with bookedSlots and instanceIds
   */
  async getAvailability(spaceId, from = null, to = null, spaceInstanceId = null) {
    try {
      const response = await spaceManagementService.getBookingAvailability({
        spaceId: Number(spaceId),
        from: from || undefined,
        to: to || undefined,
        spaceInstanceId: spaceInstanceId ? Number(spaceInstanceId) : undefined,
      });

      logger.log("[AvailabilityService] Fetched availability:", {
        spaceId,
        from,
        to,
        instanceId: spaceInstanceId,
        totalInstances: response.data?.totalInstances,
        bookedSlotsCount: response.data?.bookedSlots?.length || 0,
      });

      return response.data;
    } catch (error) {
      logger.error("[AvailabilityService] Failed to fetch availability:", error);
      throw error;
    }
  }

  /**
   * Get availability for a specific date (HOURLY bookings)
   * @param {number} spaceId - Space ID
   * @param {Date} date - Date to check
   * @param {number} spaceInstanceId - Optional instance ID
   * @returns {Promise<Object>} Availability data for the date
   */
  async getAvailabilityForDate(spaceId, date, spaceInstanceId = null) {
    const dateStr = date.toISOString().split("T")[0];
    return this.getAvailability(spaceId, dateStr, dateStr, spaceInstanceId);
  }

  /**
   * Get availability for a date range (DAILY/WEEKLY/MONTHLY bookings)
   * @param {number} spaceId - Space ID
   * @param {Date} startDate - Range start date
   * @param {Date} endDate - Range end date
   * @param {number} spaceInstanceId - Optional instance ID
   * @returns {Promise<Object>} Availability data for the range
   */
  async getAvailabilityForRange(spaceId, startDate, endDate, spaceInstanceId = null) {
    const fromStr = startDate.toISOString().split("T")[0];
    const toStr = endDate.toISOString().split("T")[0];
    return this.getAvailability(spaceId, fromStr, toStr, spaceInstanceId);
  }

  /**
   * Get availability for next N days (default 90 days)
   * @param {number} spaceId - Space ID
   * @param {number} days - Number of days to fetch (default 90)
   * @param {number} spaceInstanceId - Optional instance ID
   * @returns {Promise<Object>} Availability data
   */
  async getAvailabilityForNextDays(spaceId, days = 90, spaceInstanceId = null) {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + days);

    return this.getAvailabilityForRange(spaceId, today, futureDate, spaceInstanceId);
  }

  /**
   * Check if a time slot is available
   * @param {Object} availabilityData - Response from getAvailability
   * @param {Date} startTime - Slot start time
   * @param {Date} endTime - Slot end time
   * @param {number} instanceId - Optional instance ID to check
   * @param {number} bufferMinutes - Buffer time in minutes (default 0)
   * @returns {boolean} True if slot is available
   */
  isSlotAvailable(availabilityData, startTime, endTime, instanceId = null, bufferMinutes = 0) {
    if (!availabilityData?.bookedSlots || availabilityData.bookedSlots.length === 0) {
      return true;
    }

    // If specific instance is requested, check only that instance
    if (instanceId) {
      return !this.checkInstanceConflict(availabilityData.bookedSlots, startTime, endTime, instanceId, bufferMinutes);
    }

    // If no specific instance, check if ANY instance is available
    // Count how many instances are booked for this slot
    const totalInstances = availabilityData.totalInstances || availabilityData.instanceIds?.length || 1;
    const busyInstancesCount = this.countConflictingInstances(
      availabilityData.bookedSlots,
      startTime,
      endTime,
      bufferMinutes
    );

    return busyInstancesCount < totalInstances;
  }

  /**
   * Check if a time point is within a booked slot
   * @param {Object} availabilityData - Response from getAvailability
   * @param {Date} timePoint - Time point to check
   * @param {number} instanceId - Optional instance ID to check
   * @param {number} bufferMinutes - Buffer time in minutes (default 0)
   * @returns {boolean} True if time point is booked (all instances full)
   */
  isTimePointBooked(availabilityData, timePoint, instanceId = null, bufferMinutes = 0) {
    if (!availabilityData?.bookedSlots || availabilityData.bookedSlots.length === 0) {
      return false;
    }

    const startTime = timePoint;
    const endTime = new Date(timePoint.getTime() + 1000); // Check 1 second window

    if (instanceId) {
      return this.checkInstanceConflict(availabilityData.bookedSlots, startTime, endTime, instanceId, bufferMinutes);
    }

    const totalInstances = availabilityData.totalInstances || availabilityData.instanceIds?.length || 1;
    const busyInstancesCount = this.countConflictingInstances(
      availabilityData.bookedSlots,
      startTime,
      endTime,
      bufferMinutes
    );

    return busyInstancesCount >= totalInstances;
  }

  /**
   * Helper: Check if a specific instance has a conflict
   */
  checkInstanceConflict(bookedSlots, startTime, endTime, instanceId, bufferMinutes = 0) {
    const slotStart = startTime.getTime();
    const slotEnd = endTime.getTime();
    const bufferMs = bufferMinutes * 60 * 1000;

    return bookedSlots.some(booking => {
      // Skip if booking is for a different instance
      if (booking.spaceInstanceId && booking.spaceInstanceId !== instanceId) {
        return false;
      }

      // If booking has NO instance ID, it blocks everything (legacy/global booking)
      if (!booking.spaceInstanceId) {
        return true;
      }

      const bookingStart = new Date(booking.startTime).getTime();
      const bookingEnd = new Date(booking.endTime).getTime();
      const bookingEndWithBuffer = bookingEnd + bufferMs;

      return slotStart < bookingEndWithBuffer && slotEnd > bookingStart;
    });
  }

  /**
   * Helper: Count how many unique instances are busy during a time slot
   */
  countConflictingInstances(bookedSlots, startTime, endTime, bufferMinutes = 0) {
    const slotStart = startTime.getTime();
    const slotEnd = endTime.getTime();
    const bufferMs = bufferMinutes * 60 * 1000;
    const busyInstanceIds = new Set();

    bookedSlots.forEach(booking => {
      const bookingStart = new Date(booking.startTime).getTime();
      const bookingEnd = new Date(booking.endTime).getTime();
      const bookingEndWithBuffer = bookingEnd + bufferMs;

      // Check overlap
      if (slotStart < bookingEndWithBuffer && slotEnd > bookingStart) {
        if (booking.spaceInstanceId) {
          busyInstanceIds.add(booking.spaceInstanceId);
        } else {
          // Booking with no instance ID counts as blocking ALL instances?
          // Or at least one? Let's assume it blocks one "virtual" instance if we are counting.
          // But usually a booking without instance ID in a multi-instance space is a data error or blocks all.
          // For safety in this logic, if we encounter a global booking, we might need to assume it consumes 1 capacity
          // unless your business logic says it blocks everything.
          // Let's assume it consumes 1 capacity for now by adding a distinct marker.
          busyInstanceIds.add(`legacy-booking-${booking.id || Math.random()}`);
        }
      }
    });

    return busyInstanceIds.size;
  }

  /**
   * Get booked dates from availability data
   * @param {Object} availabilityData - Response from getAvailability
   * @param {number} instanceId - Optional instance ID to filter
   * @returns {Set<string>} Set of booked date strings (toDateString format)
   */
  getBookedDates(availabilityData, instanceId = null) {
    if (!availabilityData?.bookedSlots || availabilityData.bookedSlots.length === 0) {
      return new Set();
    }

    const bookedDates = new Set();

    availabilityData.bookedSlots.forEach(booking => {
      // Filter by instance if specified
      if (instanceId && booking.spaceInstanceId && booking.spaceInstanceId !== instanceId) {
        return;
      }

      const start = new Date(booking.startTime);
      const end = new Date(booking.endTime);
      const current = new Date(start);

      // Add all dates within the booking range
      while (current <= end) {
        bookedDates.add(current.toDateString());
        current.setDate(current.getDate() + 1);
      }
    });

    return bookedDates;
  }

  /**
   * Get bookings for a specific date
   * @param {Object} availabilityData - Response from getAvailability
   * @param {Date} date - Date to check
   * @param {number} instanceId - Optional instance ID to filter
   * @returns {Array} Array of bookings for the date
   */
  getBookingsForDate(availabilityData, date, instanceId = null) {
    if (!availabilityData?.bookedSlots || availabilityData.bookedSlots.length === 0) {
      return [];
    }

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const nextDay = new Date(checkDate);
    nextDay.setDate(nextDay.getDate() + 1);

    return availabilityData.bookedSlots.filter(booking => {
      // Filter by instance if specified
      if (instanceId && booking.spaceInstanceId && booking.spaceInstanceId !== instanceId) {
        return false;
      }

      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);

      // Booking overlaps with date if it starts before next day and ends after current day
      return bookingStart < nextDay && bookingEnd >= checkDate;
    });
  }

  /**
   * Check for conflicts in a date range
   * @param {Object} availabilityData - Response from getAvailability
   * @param {Date} startDate - Range start date
   * @param {Date} endDate - Range end date
   * @param {number} instanceId - Optional instance ID to filter
   * @returns {Array|null} Array of conflicts or null if no conflicts
   */
  checkRangeConflicts(availabilityData, startDate, endDate, instanceId = null) {
    if (!availabilityData?.bookedSlots || availabilityData.bookedSlots.length === 0) {
      return null;
    }

    const conflicts = [];
    const rangeStart = new Date(startDate);
    const rangeEnd = new Date(endDate);

    availabilityData.bookedSlots.forEach(booking => {
      // Filter by instance if specified
      if (instanceId && booking.spaceInstanceId && booking.spaceInstanceId !== instanceId) {
        return;
      }

      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);

      // Check if booking overlaps with selected range
      if (bookingStart < rangeEnd && bookingEnd > rangeStart) {
        conflicts.push({
          date: bookingStart,
          bookings: [booking],
          instanceId: booking.spaceInstanceId,
        });
      }
    });

    return conflicts.length > 0 ? conflicts : null;
  }

  /**
   * Transform booked slots into calendar grid format (for SpaceCalendarView)
   * @param {Array} bookedSlots - Array of booked slots from API
   * @param {Array} timeSlots - Array of time slot strings (e.g., ["09:00", "09:30", ...])
   * @param {Array} days - Array of day objects with date info
   * @param {number} instanceId - Optional instance ID to filter
   * @returns {Object} Grid object with time slots as keys and day arrays as values
   */
  transformToCalendarGrid(bookedSlots, timeSlots, days, instanceId = null) {
    const grid = {};

    // Initialize grid with all slots as available (✓)
    timeSlots.forEach(time => {
      grid[time] = days.map(() => "✓");
    });

    if (!bookedSlots || bookedSlots.length === 0) {
      return grid;
    }

    // Mark booked slots
    bookedSlots.forEach(booking => {
      // Filter by instance if specified
      if (instanceId && booking.spaceInstanceId && booking.spaceInstanceId !== instanceId) {
        return;
      }

      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);

      // For each day in the week
      days.forEach((day, dayIndex) => {
        const dayDate = new Date(day.fullDate || day.date);
        const dayStart = new Date(dayDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayDate);
        dayEnd.setHours(23, 59, 59, 999);

        // Check if booking overlaps with this day
        if (bookingStart <= dayEnd && bookingEnd >= dayStart) {
          // Mark all time slots within booking as booked
          timeSlots.forEach(timeSlot => {
            const [hours, minutes] = timeSlot.split(":").map(Number);
            const slotTime = new Date(dayDate);
            slotTime.setHours(hours, minutes, 0, 0);

            // Check if slot time is within booking
            if (slotTime >= bookingStart && slotTime < bookingEnd) {
              grid[timeSlot][dayIndex] = "B"; // Booked
            }
          });
        }
      });
    });

    return grid;
  }

  /**
   * Get instance availability summary for a date
   * @param {Object} availabilityData - Response from getAvailability
   * @param {Date} date - Date to check
   * @returns {Array} Array of instance availability objects
   */
  getInstanceAvailability(availabilityData, date) {
    if (!availabilityData?.instanceIds || availabilityData.instanceIds.length === 0) {
      return [];
    }

    return availabilityData.instanceIds.map((instanceId, index) => {
      const bookings = this.getBookingsForDate(availabilityData, date, instanceId);

      return {
        id: instanceId,
        title: `Unit #${index + 1}`,
        bookingsCount: bookings.length,
        isFullyBooked: bookings.length > 0, // Simplified - could be more sophisticated
        bookings: bookings,
      };
    });
  }
}

export default new AvailabilityService();
